import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Calendar, Euro, ChevronLeft, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import eventService from '../services/eventService';
import authService from '../services/authService';
import EventBookingModal from './EventBookingModal';
import './EventsDisplay.css';
import CustomAlert from './CustomAlert';
import useCustomAlert from '../hooks/useCustomAlert';

const PLACEHOLDER_IMAGE = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Crect fill="%23f0f0f0" width="400" height="300"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="24" fill="%23999"%3ECarregando...%3C/text%3E%3C/svg%3E';

const EventCard = React.memo(({
  event,
  currentImageIndex,
  onPreviousImage,
  onNextImage,
  onOpenModal,
  formatDate,
  formatPrice,
  eventImages
}) => {
  const eventData = useMemo(() => {
    const allImages = [];

    if (eventImages && eventImages.images && eventImages.images.length > 0) {
      allImages.push(...eventImages.images);
    }

    if (eventImages && eventImages.dailyDetails && eventImages.dailyDetails.length > 0) {
      eventImages.dailyDetails.forEach(detail => {
        if (detail.image && detail.image.trim() !== '') {
          allImages.push(detail.image);
        }
      });
    }

    if (allImages.length === 0) {
      allImages.push(PLACEHOLDER_IMAGE);
    }

    const isMultiDay = event.endDate && event.startDate !== event.endDate;
    const totalDays = !event.endDate ? 1 : eventService.calculateDuration(event.startDate, event.endDate);

    return {
      allImages,
      isMultiDay,
      totalDays,
      hasMultipleImages: allImages.length > 1 && allImages[0] !== PLACEHOLDER_IMAGE
    };
  }, [event.id, event.startDate, event.endDate, eventImages]);

  const currentIndex = currentImageIndex || 0;

  const handlePrevious = useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();
    onPreviousImage(event.id, eventData.allImages.length);
  }, [event.id, eventData.allImages.length, onPreviousImage]);

  const handleNext = useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();
    onNextImage(event.id, eventData.allImages.length);
  }, [event.id, eventData.allImages.length, onNextImage]);

  const handleOpenModal = useCallback(() => {
    onOpenModal(event);
  }, [event, onOpenModal]);

  return (
    <div className="event-card">
      <div className="event-image-wrapper">
        <img
          src={eventData.allImages[currentIndex]}
          alt={event.title}
          className="event-image"
          loading="lazy"
          decoding="async"
        />

        <div className="event-type-badge">
          {eventData.isMultiDay ? `${eventData.totalDays} dias` : 'Dia único'}
        </div>

        {eventData.hasMultipleImages && (
          <>
            <button
              onClick={handlePrevious}
              className="carousel-arrow carousel-arrow-left"
              aria-label="Imagem anterior"
            >
              <ChevronLeft size={20} />
            </button>

            <button
              onClick={handleNext}
              className="carousel-arrow carousel-arrow-right"
              aria-label="Próxima imagem"
            >
              <ChevronRight size={20} />
            </button>

            <div className="carousel-dots">
              {eventData.allImages.map((_, index) => (
                <span
                  key={index}
                  className={`carousel-dot ${index === currentIndex ? 'active' : ''}`}
                />
              ))}
            </div>
          </>
        )}
      </div>

      <div className="event-content">
        <h3 className="event-title">{event.title}</h3>
        <p className="event-description">{event.description}</p>

        <div className="event-info">
          <div className="info-item">
            <Calendar size={16} />
            <span>
              {formatDate(event.startDate)}
              {eventData.isMultiDay && ` - ${formatDate(event.endDate)}`}
            </span>
          </div>

          <div className="info-item">
            <Euro size={16} />
            <span className="price">{formatPrice(event.price)}</span>
          </div>
        </div>

        <button
          onClick={handleOpenModal}
          className="event-button"
          disabled={event.availableSpots === 0}
        >
          {event.availableSpots === 0 ? 'Esgotado' : 'Reservar Agora'}
        </button>
      </div>
    </div>
  );
});

EventCard.displayName = 'EventCard';

export default function EventsDisplay({ showTitle = true, limit = null }) {
  const [events, setEvents] = useState([]);
  const [eventImages, setEventImages] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const { alertProps, showWarning, showSuccess } = useCustomAlert();
  const [currentImageIndex, setCurrentImageIndex] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    try {
      setLoading(true);
      const data = await eventService.getEventsOptimized();
      const eventsToShow = limit ? data.slice(0, limit) : data;
      setEvents(eventsToShow);
      setLoading(false);
      loadEventImages(eventsToShow);
    } catch (err) {
      console.error('Erro ao carregar eventos:', err);
      setError('Não foi possível carregar os eventos');
      setLoading(false);
    }
  };

  const loadEventImages = async (eventsToLoad) => {
    const imagePromises = eventsToLoad.map(event =>
      eventService.getEventImages(event.id)
        .then(images => ({ id: event.id, images }))
        .catch(() => ({ id: event.id, images: { images: [], dailyDetails: [] } }))
    );

    const results = await Promise.all(imagePromises);
    const imagesMap = {};
    results.forEach(result => imagesMap[result.id] = result.images);
    setEventImages(imagesMap);
  };

  const goToPreviousImage = useCallback((eventId, totalImages) => {
    setCurrentImageIndex(prev => ({
      ...prev,
      [eventId]: ((prev[eventId] || 0) - 1 + totalImages) % totalImages
    }));
  }, []);

  const goToNextImage = useCallback((eventId, totalImages) => {
    setCurrentImageIndex(prev => ({
      ...prev,
      [eventId]: ((prev[eventId] || 0) + 1) % totalImages
    }));
  }, []);

  const handleOpenModal = useCallback((event) => {
    if (!authService.isAuthenticated()) {
      navigate('/login');
      return;
    }
    setSelectedEvent(event);
    setIsModalOpen(true);
  }, [navigate]);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedEvent(null);
  }, []);

  const formatDate = useCallback((dateString) => {
    return new Date(dateString).toLocaleDateString('pt-PT', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  }, []);

  const formatPrice = useCallback((price) => {
    return typeof price === 'number' ? price.toFixed(2) : parseFloat(price).toFixed(2);
  }, []);

  if (loading) {
    return (
      <div className="events-display">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>A carregar eventos...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="events-display">
        <div className="error-state">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!events || events.length === 0) {
    return (
      <div className="events-display">
        {showTitle && <h2 className="events-section-title">Próximos Eventos</h2>}
        <div className="empty-state">
          <Calendar size={64} />
          <h3>Não há eventos disponíveis</h3>
          <p>Novos eventos serão publicados em breve!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="events-display">
      <CustomAlert {...alertProps} />

      {showTitle && <h2 className="events-title">Próximos Eventos</h2>}

      <div className="events-grid">
        {events.map(event => (
          <EventCard
            key={event.id}
            event={event}
            currentImageIndex={currentImageIndex[event.id]}
            onPreviousImage={goToPreviousImage}
            onNextImage={goToNextImage}
            onOpenModal={handleOpenModal}
            formatDate={formatDate}
            formatPrice={formatPrice}
            eventImages={eventImages[event.id]}
          />
        ))}
      </div>

      {isModalOpen && selectedEvent && (
        <EventBookingModal
          event={selectedEvent}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          showSuccess={showSuccess}
          showWarning={showWarning}
        />
      )}
    </div>
  );
}